/**
 * Compliance Document Detail — single-doc page.
 *
 * Surfaces:
 *  - Metadata editor (title, category, tags, owner, review dates, linked jobs/sites/assets, notes)
 *  - PDF viewer (react-pdf) for the current version
 *  - Annotation overlay (comment pins) that any reader can add / edit / delete
 *  - Version history with download / restore controls
 *
 * Roles:
 *  - Admin (super_admin / admin / project_manager): full read/write
 *  - In-house: read-only metadata + viewer, but CAN add comment-pin annotations
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  ShieldCheck, ArrowLeft, DownloadSimple, UploadSimple, CaretLeft, CaretRight,
  Plus, Minus, FloppyDisk, ArrowsClockwise, ChatCircle, X, PencilSimple,
} from "@phosphor-icons/react";
import { downloadAuthFile } from "../lib/fileOpen";

import DateInput from "../components/DateInput";
// Wire pdf.js worker. Using a CDN worker keeps the bundle small.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ADMIN_ROLES = ["super_admin", "admin", "project_manager"];

export default function ComplianceDocumentDetailPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docRes, versionsRes, catsRes] = await Promise.all([
        API.get(`/compliance-hub/documents/${docId}`),
        API.get(`/compliance-hub/documents/${docId}/versions`),
        API.get("/compliance-hub/categories"),
      ]);
      setDoc(docRes.data);
      setVersions(versionsRes.data?.versions || []);
      setCategories(catsRes.data?.categories || []);
    } catch (e) {
      toast.error("Failed to load: " + (e.response?.data?.detail || e.message));
      if (e.response?.status === 404) navigate("/compliance-hub");
    } finally {
      setLoading(false);
    }
  }, [docId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Fetch the PDF bytes through our axios instance (carries the bearer token)
  // and hand react-pdf a local blob URL.
  useEffect(() => {
    let cancelled = false;
    let urlToRevoke = null;
    async function fetchPdf() {
      if (!doc?.current_file_id) { setPdfBlobUrl(null); return; }
      try {
        const res = await API.get(`/files/${doc.current_file_id}/preview`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        urlToRevoke = url;
        setPdfBlobUrl(url);
      } catch (e) {
        if (!cancelled) toast.error("Could not load PDF: " + (e.response?.data?.detail || e.message));
      }
    }
    fetchPdf();
    return () => {
      cancelled = true;
      if (urlToRevoke) setTimeout(() => URL.revokeObjectURL(urlToRevoke), 60_000);
    };
  }, [doc?.current_file_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!doc) return null;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="compliance-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <Button variant="ghost" size="sm" onClick={() => navigate("/compliance-hub")} data-testid="compliance-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold mt-2 flex items-center gap-2 font-[Chivo]">
            <ShieldCheck size={22} weight="duotone" className="text-primary" />
            <span className="truncate">{doc.title}</span>
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge variant="outline">{categories.find(c => c.id === doc.category)?.label || doc.category}</Badge>
            {doc.current_version_number > 0 && <Badge variant="secondary">v{doc.current_version_number}</Badge>}
            {doc.owner_name && <span className="text-xs text-muted-foreground">Owner: {doc.owner_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {doc.current_file_id && (
            <Button
              variant="outline"
              onClick={() => downloadAuthFile(`/api/files/${doc.current_file_id}/download`, doc.current_file_name)}
              data-testid="compliance-download-btn"
            >
              <DownloadSimple size={16} className="mr-1" /> Download
            </Button>
          )}
          {isAdmin && <NewVersionButton docId={doc.id} onUploaded={load} />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Main PDF viewer + annotations */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {pdfBlobUrl ? (
            <PdfViewer
              docId={doc.id}
              fileUrl={pdfBlobUrl}
              annotations={doc.annotations || []}
              onAnnotationsChange={(next) => setDoc(d => ({ ...d, annotations: next }))}
              currentUserId={String(user?.id || user?._id || "")}
              isAdmin={isAdmin}
            />
          ) : (
            <EmptyPdfState isAdmin={isAdmin} docId={doc.id} onUploaded={load} />
          )}
        </div>

        {/* Side panel: metadata + versions */}
        <Tabs defaultValue="metadata">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="metadata" data-testid="compliance-tab-metadata">Details</TabsTrigger>
            <TabsTrigger value="versions" data-testid="compliance-tab-versions">Versions ({versions.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="metadata">
            <MetadataPanel
              doc={doc}
              categories={categories}
              isAdmin={isAdmin}
              onSaved={(next) => setDoc(next)}
            />
          </TabsContent>
          <TabsContent value="versions">
            <VersionsPanel
              docId={doc.id}
              versions={versions}
              currentVersionId={doc.current_version_id}
              isAdmin={isAdmin}
              onChanged={load}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ------------------------- PDF Viewer ------------------------- */

function EmptyPdfState({ isAdmin, docId, onUploaded }) {
  return (
    <div className="p-16 text-center">
      <ShieldCheck size={48} className="mx-auto text-muted-foreground mb-3" />
      <p className="font-semibold mb-1">No PDF uploaded yet</p>
      <p className="text-sm text-muted-foreground mb-4">
        {isAdmin ? "Upload the first version of this document." : "An admin needs to upload the PDF for this document."}
      </p>
      {isAdmin && <NewVersionButton docId={docId} onUploaded={onUploaded} initial />}
    </div>
  );
}

/**
 * react-pdf wrapper with:
 *   - page nav (prev / next)
 *   - zoom controls
 *   - tap-anywhere-to-comment (overlay layer drops a pin)
 *   - pin click reveals editable comment popover
 */
function PdfViewer({ docId, fileUrl, annotations, onAnnotationsChange, currentUserId, isAdmin }) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [annotateMode, setAnnotateMode] = useState(false);
  const [openAnnotation, setOpenAnnotation] = useState(null); // {id} or {pending:true}
  const [width, setWidth] = useState(800);
  const containerRef = useRef(null);

  // Re-measure container so the PDF fills the available width.
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const pageAnnotations = useMemo(
    () => (annotations || []).filter(a => a.page === pageNumber),
    [annotations, pageNumber],
  );

  const onOverlayClick = async (e) => {
    if (!annotateMode) return;
    // Compute rect % so the pin scales with zoom / re-render.
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    try {
      const res = await API.post(`/compliance-hub/documents/${docId}/annotations`, {
        page: pageNumber,
        type: "comment",
        rect: [x, y, 0, 0],
        content: "",
        color: "#fbbf24",
      });
      onAnnotationsChange([...(annotations || []), res.data]);
      setOpenAnnotation({ id: res.data.id });
      setAnnotateMode(false);
    } catch (err) {
      toast.error("Could not add comment: " + (err.response?.data?.detail || err.message));
    }
  };

  const onSaveAnnotation = async (id, content) => {
    try {
      const res = await API.put(`/compliance-hub/documents/${docId}/annotations/${id}`, { content });
      onAnnotationsChange((annotations || []).map(a => a.id === id ? res.data : a));
      setOpenAnnotation(null);
    } catch (err) {
      toast.error("Could not save comment: " + (err.response?.data?.detail || err.message));
    }
  };

  const onDeleteAnnotation = async (id) => {
    try {
      await API.delete(`/compliance-hub/documents/${docId}/annotations/${id}`);
      onAnnotationsChange((annotations || []).filter(a => a.id !== id));
      setOpenAnnotation(null);
    } catch (err) {
      toast.error("Could not delete comment: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30 flex-wrap">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1} data-testid="compliance-prev-page">
            <CaretLeft size={14} />
          </Button>
          <span className="text-xs font-medium tabular-nums" data-testid="compliance-page-num">
            Page {pageNumber} / {numPages || "—"}
          </span>
          <Button size="sm" variant="ghost" onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages} data-testid="compliance-next-page">
            <CaretRight size={14} />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setScale(s => Math.max(0.5, s - 0.15))} data-testid="compliance-zoom-out">
            <Minus size={14} />
          </Button>
          <span className="text-xs tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
          <Button size="sm" variant="ghost" onClick={() => setScale(s => Math.min(2.5, s + 0.15))} data-testid="compliance-zoom-in">
            <Plus size={14} />
          </Button>
        </div>
        <Button
          size="sm"
          variant={annotateMode ? "default" : "outline"}
          onClick={() => setAnnotateMode(m => !m)}
          data-testid="compliance-annotate-toggle"
        >
          <ChatCircle size={14} className="mr-1" /> {annotateMode ? "Tap on PDF to drop comment" : "Add comment"}
        </Button>
      </div>

      {/* PDF + overlay */}
      <div
        ref={containerRef}
        className="bg-muted/10 overflow-auto max-h-[75vh] flex justify-center"
        data-testid="compliance-pdf-container"
      >
        <div className="relative">
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={(e) => toast.error("PDF load failed: " + e.message)}
            loading={<div className="p-12 text-sm text-muted-foreground">Loading PDF…</div>}
          >
            <Page pageNumber={pageNumber} width={Math.min(width - 16, 900) * scale} />
          </Document>
          {/* Click-to-comment overlay: only intercepts events when in annotate mode. */}
          <div
            className="absolute inset-0"
            style={{ cursor: annotateMode ? "crosshair" : "default", pointerEvents: annotateMode ? "auto" : "none" }}
            onClick={onOverlayClick}
            data-testid="compliance-pdf-overlay"
          />
          {/* Pins layer (always rendered above PDF) */}
          <div className="absolute inset-0 pointer-events-none">
            {pageAnnotations.map(a => (
              <AnnotationPin
                key={a.id}
                annotation={a}
                isOpen={openAnnotation?.id === a.id}
                onOpen={() => setOpenAnnotation({ id: a.id })}
                onClose={() => setOpenAnnotation(null)}
                onSave={(content) => onSaveAnnotation(a.id, content)}
                onDelete={() => onDeleteAnnotation(a.id)}
                canEdit={isAdmin || a.author_id === currentUserId}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnnotationPin({ annotation, isOpen, onOpen, onClose, onSave, onDelete, canEdit }) {
  const [editing, setEditing] = useState(!annotation.content && canEdit);
  const [value, setValue] = useState(annotation.content || "");
  useEffect(() => { setValue(annotation.content || ""); setEditing(!annotation.content && canEdit); }, [annotation.id, annotation.content, canEdit]);
  return (
    <div
      className="absolute pointer-events-auto"
      style={{ left: `${annotation.rect[0]}%`, top: `${annotation.rect[1]}%`, transform: "translate(-50%, -100%)" }}
    >
      <button
        className="h-6 w-6 rounded-full bg-amber-400 border-2 border-white shadow-lg flex items-center justify-center text-amber-900 hover:scale-110 transition"
        onClick={() => (isOpen ? onClose() : onOpen())}
        title={annotation.content || "Comment"}
        data-testid={`compliance-pin-${annotation.id}`}
      >
        <ChatCircle size={12} weight="bold" />
      </button>
      {isOpen && (
        <div className="absolute left-1/2 top-7 -translate-x-1/2 bg-popover border border-border rounded-lg shadow-lg p-3 w-64 z-20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-muted-foreground">
              {annotation.author_name} · {(annotation.created_at || "").slice(0, 10)}
            </p>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X size={12} />
            </button>
          </div>
          {editing ? (
            <>
              <Textarea
                value={value}
                onChange={e => setValue(e.target.value)}
                rows={3}
                placeholder="Write a comment…"
                autoFocus
                data-testid="compliance-pin-textarea"
              />
              <div className="flex justify-end gap-1 mt-2">
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600" data-testid="compliance-pin-delete">
                    Delete
                  </Button>
                )}
                <Button size="sm" onClick={() => onSave(value)} data-testid="compliance-pin-save">Save</Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm whitespace-pre-wrap break-words">{annotation.content || <em className="text-muted-foreground">No comment</em>}</p>
              {canEdit && (
                <div className="flex justify-end gap-1 mt-2">
                  <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600" data-testid="compliance-pin-delete">
                    Delete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="compliance-pin-edit">
                    <PencilSimple size={12} className="mr-1" /> Edit
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Side Panels ------------------------- */

function MetadataPanel({ doc, categories, isAdmin, onSaved }) {
  const [draft, setDraft] = useState(doc);
  const [tagsRaw, setTagsRaw] = useState((doc.tags || []).join(", "));
  const [linkedJobsRaw, setLinkedJobsRaw] = useState((doc.linked_jobs || []).join(", "));
  const [linkedSitesRaw, setLinkedSitesRaw] = useState((doc.linked_sites || []).join(", "));
  const [linkedAssetsRaw, setLinkedAssetsRaw] = useState((doc.linked_assets || []).join(", "));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(doc);
    setTagsRaw((doc.tags || []).join(", "));
    setLinkedJobsRaw((doc.linked_jobs || []).join(", "));
    setLinkedSitesRaw((doc.linked_sites || []).join(", "));
    setLinkedAssetsRaw((doc.linked_assets || []).join(", "));
  }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        category: draft.category,
        notes: draft.notes,
        last_reviewed_date: draft.last_reviewed_date || null,
        next_review_date: draft.next_review_date || null,
        tags: tagsRaw.split(",").map(s => s.trim()).filter(Boolean),
        linked_jobs: linkedJobsRaw.split(",").map(s => s.trim()).filter(Boolean),
        linked_sites: linkedSitesRaw.split(",").map(s => s.trim()).filter(Boolean),
        linked_assets: linkedAssetsRaw.split(",").map(s => s.trim()).filter(Boolean),
      };
      const res = await API.put(`/compliance-hub/documents/${doc.id}`, payload);
      toast.success("Details saved");
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const readonly = !isAdmin;

  // Read-only view for non-admin (in-house operatives) — clean key/value
  // display rather than a row of disabled-looking inputs.
  if (readonly) {
    const cat = categories.find(c => c.id === doc.category);
    return (
      <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="compliance-metadata-panel">
        <ReadField label="Title" value={doc.title} />
        <ReadField label="Category" value={cat?.label || doc.category} />
        <ReadField label="Owner" value={doc.owner_name || "—"} />
        <div className="grid grid-cols-2 gap-2">
          <ReadField label="Last reviewed" value={(doc.last_reviewed_date || "").slice(0, 10) || "—"} />
          <ReadField label="Next review" value={(doc.next_review_date || "").slice(0, 10) || "—"} />
        </div>
        <ReadField label="Tags" value={(doc.tags || []).length ? doc.tags.map(t => `#${t}`).join(" ") : "—"} />
        <ReadField label="Linked jobs" value={(doc.linked_jobs || []).join(", ") || "—"} mono />
        <ReadField label="Linked sites" value={(doc.linked_sites || []).join(", ") || "—"} />
        <ReadField label="Linked assets" value={(doc.linked_assets || []).join(", ") || "—"} />
        <ReadField label="Notes" value={doc.notes || "—"} multiline />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="compliance-metadata-panel">
      <Field label="Title">
        <Input value={draft.title || ""} onChange={e => setField("title", e.target.value)} data-testid="compliance-meta-title" />
      </Field>
      <Field label="Category">
        <Select value={draft.category || ""} onValueChange={v => setField("category", v)}>
          <SelectTrigger data-testid="compliance-meta-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Owner">
        <Input value={draft.owner_name || ""} onChange={e => setField("owner_name", e.target.value)} data-testid="compliance-meta-owner" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Last reviewed">
          <DateInput value={(draft.last_reviewed_date || "").slice(0, 10)} onChange={e => setField("last_reviewed_date", e.target.value)} data-testid="compliance-meta-last-reviewed" />
        </Field>
        <Field label="Next review">
          <DateInput value={(draft.next_review_date || "").slice(0, 10)} onChange={e => setField("next_review_date", e.target.value)} data-testid="compliance-meta-next-review" />
        </Field>
      </div>
      <Field label="Tags (comma separated)">
        <Input value={tagsRaw} onChange={e => setTagsRaw(e.target.value)} data-testid="compliance-meta-tags" />
      </Field>
      <Field label="Linked job IDs (comma separated)">
        <Input value={linkedJobsRaw} onChange={e => setLinkedJobsRaw(e.target.value)} data-testid="compliance-meta-jobs" placeholder="job-uuid-1, job-uuid-2" />
      </Field>
      <Field label="Linked sites">
        <Input value={linkedSitesRaw} onChange={e => setLinkedSitesRaw(e.target.value)} data-testid="compliance-meta-sites" placeholder="Site name or address" />
      </Field>
      <Field label="Linked assets">
        <Input value={linkedAssetsRaw} onChange={e => setLinkedAssetsRaw(e.target.value)} data-testid="compliance-meta-assets" />
      </Field>
      <Field label="Notes">
        <Textarea value={draft.notes || ""} rows={3} onChange={e => setField("notes", e.target.value)} data-testid="compliance-meta-notes" />
      </Field>
      <Button onClick={save} disabled={saving} className="w-full" data-testid="compliance-meta-save">
        <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save details"}
      </Button>
    </div>
  );
}

function ReadField({ label, value, mono, multiline }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm ${mono ? "font-mono text-xs" : ""} ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function VersionsPanel({ docId, versions, currentVersionId, isAdmin, onChanged }) {
  const onRestore = async (v) => {
    if (!isAdmin) return;
    if (!window.confirm(`Make v${v.version_number} the current version?`)) return;
    try {
      await API.post(`/compliance-hub/documents/${docId}/versions/${v.id}/restore`);
      toast.success(`Restored v${v.version_number}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error("Restore failed: " + (e.response?.data?.detail || e.message));
    }
  };
  if (versions.length === 0) {
    return <p className="text-sm text-muted-foreground p-4 text-center">No versions yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="compliance-versions-panel">
      {versions.map(v => {
        const isCurrent = v.id === currentVersionId;
        return (
          <div
            key={v.id}
            className={`border rounded-lg p-3 ${isCurrent ? "border-primary bg-primary/5" : "border-border bg-card"}`}
            data-testid={`compliance-version-${v.version_number}`}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="font-semibold text-sm">
                v{v.version_number} {isCurrent && <Badge variant="default" className="ml-1 text-[9px]">Current</Badge>}
              </p>
              <p className="text-[10px] text-muted-foreground">{(v.uploaded_at || "").slice(0, 10)}</p>
            </div>
            <p className="text-xs text-muted-foreground truncate" title={v.file_name}>{v.file_name}</p>
            {v.change_note && <p className="text-xs italic mt-1">&ldquo;{v.change_note}&rdquo;</p>}
            <p className="text-[10px] text-muted-foreground mt-1">by {v.uploaded_by_name}</p>
            <div className="flex items-center gap-1 mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadAuthFile(`/api/files/${v.file_id}/download`, v.file_name)}
                data-testid={`compliance-version-download-${v.version_number}`}
              >
                <DownloadSimple size={12} className="mr-1" /> Download
              </Button>
              {!isCurrent && isAdmin && (
                <Button size="sm" variant="ghost" onClick={() => onRestore(v)} data-testid={`compliance-version-restore-${v.version_number}`}>
                  <ArrowsClockwise size={12} className="mr-1" /> Make current
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewVersionButton({ docId, onUploaded, initial = false }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Choose a PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("change_note", note);
      await API.post(`/compliance-hub/documents/${docId}/versions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Version uploaded");
      setOpen(false);
      setFile(null);
      setNote("");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="compliance-new-version-btn">
        <UploadSimple size={16} className="mr-1" /> {initial ? "Upload PDF" : "New version"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{initial ? "Upload PDF" : "Upload a new version"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">PDF file</label>
              <Input type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="compliance-version-file" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">What changed? (optional)</label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Updated PPE section after audit" data-testid="compliance-version-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !file} data-testid="compliance-version-submit">
              {busy ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
